import React, {
    useRef,
    useState,
    useEffect,
    useMemo,
    useCallback,
} from 'react';
import { Modal } from 'react-bootstrap';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import { ReactComponent as Clock } from '../../assets/image/clock.svg';
import { ReactComponent as MapLinkIcon } from '../../assets/image/map_link.svg';
import { ReactComponent as CloseIcon } from '../../assets/image/close_small.svg';
import { ReactComponent as ActivityChevronRight } from '../../assets/image/activity_chevron_right.svg';
import {
    computeActivityCardText,
    formatHeureQuebec,
    formatRainNoteLine,
} from '../../utils/activityCardDisplay.js';
import variables from '../../variables.js';

import './ActivityDetailsModal.scss';

function primaryArtistIndexInStrip(stripEl) {
    const cards = stripEl.querySelectorAll('.activityDetailsModal__artistCard');
    if (!cards.length) return 0;
    const s = stripEl.getBoundingClientRect();
    let best = 0;
    let maxVis = 0;
    for (let i = 0; i < cards.length; i += 1) {
        const c = cards[i].getBoundingClientRect();
        const vis = Math.max(
            0,
            Math.min(c.right, s.right) - Math.max(c.left, s.left),
        );
        if (vis > maxVis) {
            maxVis = vis;
            best = i;
        }
    }
    return best;
}

function buildArtistSlides(artistNames, thumbnails) {
    const names = Array.isArray(artistNames) ? artistNames : [];
    const slides = Array.isArray(thumbnails) ? thumbnails : [];
    return names.map((name) => {
        const match = slides.find((s) =>
            (s.artists || []).some(
                (a) => String(a).trim() === String(name).trim(),
            ),
        );
        return {
            name,
            src: match ? match.src : null,
            credit: match ? match.credit : null,
        };
    });
}

function pivotToCardProps(pivot, artistNames) {
    const adresseParts = pivot
        ? [pivot.adresse_site, pivot.city, pivot.code_postal]
              .map((x) => (x == null ? '' : String(x).trim()))
              .filter(Boolean)
        : [];
    const adresse = adresseParts.join(', ');
    return {
        titre: pivot ? pivot.name : null,
        heure_debut: pivot ? pivot.heure_debut : false,
        heure_fin: pivot ? pivot.heure_fin : false,
        categorie: pivot ? pivot.type : null,
        subCategorie: pivot ? pivot.sub_type : null,
        location_nicename: pivot ? pivot.lieu_precis : '',
        adresse,
        animation: pivot ? pivot.animation : null,
        animateur: pivot ? pivot.animateur : null,
        artistNames,
        artiste: artistNames.length ? artistNames.join(', ') : null,
        chorale: pivot ? pivot.chorale : null,
        releve: pivot ? pivot.releve : null,
        representants: pivot ? pivot.representants : null,
    };
}

const ActivityDetailsModal = ({ show, onHide, activityGroup }) => {
    const stripRef = useRef(null);
    const artistSlidesRef = useRef([]);
    const announceTimerRef = useRef(null);
    const [reducedMotion, setReducedMotion] = useState(false);
    const [overflows, setOverflows] = useState(false);
    const [srCarouselArtist, setSrCarouselArtist] = useState('');

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const apply = () => setReducedMotion(mq.matches);
        apply();
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, []);

    const pivot = activityGroup && activityGroup.pivot;
    const thumbnails = activityGroup && activityGroup.thumbnails;
    const artistNames = activityGroup && activityGroup.artistNames;

    const cardLike = useMemo(
        () => pivotToCardProps(pivot, artistNames || []),
        [pivot, artistNames],
    );

    const { mainTitle, modalSubtitleLine, animTrim } = useMemo(
        () => computeActivityCardText(cardLike),
        [cardLike],
    );

    const categorie = cardLike.categorie;
    const showCategoryRow = categorie && categorie !== mainTitle;

    const timeLine =
        cardLike.heure_debut
            ? `${formatHeureQuebec(cardLike.heure_debut)}${
                  cardLike.heure_fin
                      ? `\u00a0à\u00a0${formatHeureQuebec(cardLike.heure_fin)}`
                      : ''
              }`
            : '';

    const mapsQuery = (cardLike.adresse || '').trim();
    const nicePlace = cardLike.location_nicename
        ? String(cardLike.location_nicename).trim()
        : '';
    const mapsSearch = mapsQuery || nicePlace;
    const locationName = nicePlace;
    const primaryPlaceLabel = locationName || mapsQuery;
    const addressUnderName =
        locationName &&
        mapsQuery &&
        mapsQuery.toLowerCase() !== locationName.toLowerCase()
            ? mapsQuery
            : '';
    const showPlaceBlock = Boolean(primaryPlaceLabel);

    const rainLine = pivot ? formatRainNoteLine(pivot) : '';

    const artistSlides = useMemo(
        () => buildArtistSlides(artistNames, thumbnails),
        [artistNames, thumbnails],
    );

    /** When there are no artists, still show the event activity image (card / API). */
    const eventActivityImage = useMemo(() => {
        if (Array.isArray(thumbnails) && thumbnails.length > 0 && thumbnails[0].src) {
            return {
                src: thumbnails[0].src,
                credit: thumbnails[0].credit || null,
            };
        }
        if (pivot && pivot.image_src) {
            const firstPart = String(pivot.image_src).split('|')[0];
            if (!firstPart) return null;
            return {
                src: variables.imageSource + firstPart,
                credit: pivot.credits || null,
            };
        }
        return null;
    }, [thumbnails, pivot]);

    const hasArtistStrip = artistSlides.length > 0;
    const showEventActivityImageOnly = !hasArtistStrip && !!eventActivityImage;

    useEffect(() => {
        artistSlidesRef.current = artistSlides;
    }, [artistSlides]);

    const artistStripResetKey = useMemo(() => {
        if (!pivot) return '';
        const pid = pivot.id != null ? String(pivot.id) : '';
        const names = (artistNames || []).join('\0');
        return `${pid}:${names}`;
    }, [pivot, artistNames]);

    const measureOverflow = useCallback(() => {
        const el = stripRef.current;
        if (!el) return;
        setOverflows(el.scrollWidth > el.clientWidth + 1);
    }, []);

    useEffect(() => {
        if (!show) return undefined;
        measureOverflow();
        const onResize = () => measureOverflow();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [show, artistSlides, measureOverflow]);

    // Scroll-snap was aligning the first card flush left, scrolling away the leading ::before spacer.
    // Reset to true start when opening or when artists change; re-run after layout / lazy images.
    useEffect(() => {
        if (!show) return undefined;
        const resetScrollStart = () => {
            const el = stripRef.current;
            if (el) el.scrollLeft = 0;
        };
        resetScrollStart();
        let raf2 = 0;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(resetScrollStart);
        });
        const t = window.setTimeout(resetScrollStart, 200);
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            window.clearTimeout(t);
        };
    }, [show, artistStripResetKey]);

    const clearAnnounceTimer = useCallback(() => {
        if (announceTimerRef.current != null) {
            window.clearTimeout(announceTimerRef.current);
            announceTimerRef.current = null;
        }
    }, []);

    const scheduleAnnounceVisibleArtist = useCallback(() => {
        clearAnnounceTimer();
        const delay = reducedMotion ? 80 : 450;
        announceTimerRef.current = window.setTimeout(() => {
            announceTimerRef.current = null;
            const el = stripRef.current;
            const slides = artistSlidesRef.current;
            if (!el || slides.length < 2) return;
            const idx = primaryArtistIndexInStrip(el);
            const name = slides[idx] && slides[idx].name;
            if (!name) return;
            setSrCarouselArtist('');
            requestAnimationFrame(() => {
                setSrCarouselArtist(`Artiste affiché : ${name}`);
            });
        }, delay);
    }, [clearAnnounceTimer, reducedMotion]);

    useEffect(() => {
        if (!show) {
            clearAnnounceTimer();
            setSrCarouselArtist('');
        }
    }, [show, clearAnnounceTimer]);

    useEffect(() => () => clearAnnounceTimer(), [clearAnnounceTimer]);

    const scrollArtists = useCallback(
        (dir) => {
            const el = stripRef.current;
            if (!el) return;
            const card = el.querySelector('.activityDetailsModal__artistCard');
            const gap = 24;
            const step = card ? card.offsetWidth + gap : Math.min(360, el.clientWidth * 0.85);
            el.scrollBy({
                left: dir * step,
                behavior: reducedMotion ? 'auto' : 'smooth',
            });
            if (artistSlidesRef.current.length > 1) {
                scheduleAnnounceVisibleArtist();
            }
        },
        [reducedMotion, scheduleAnnounceVisibleArtist],
    );

    const onStripKeyDown = useCallback(
        (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                scrollArtists(-1);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                scrollArtists(1);
            }
        },
        [scrollArtists],
    );

    const titleId = 'activity-details-modal-title';
    const artistStripId = 'activity-details-artist-strip';
    const artistLiveId = 'activity-details-artist-live';

    return (
        <Modal
            show={show && !!activityGroup}
            onHide={onHide}
            centered
            scrollable
            animation={!reducedMotion}
            dialogClassName="activityDetailsModal__dialog"
            contentClassName="activityDetailsModal__content"
            backdropClassName="activityDetailsModal__backdrop"
            aria-labelledby={titleId}
        >
            <div className="activityDetailsModal__chrome">
                <button
                    type="button"
                    className="activityDetailsModal__close"
                    onClick={onHide}
                    aria-label="Fermer la fenêtre de détails"
                >
                    <span className="activityDetailsModal__closeDisc" aria-hidden />
                    <CloseIcon className="activityDetailsModal__closeIcon" aria-hidden />
                </button>
                <div className="activityDetailsModal__body">
                    {showCategoryRow ? (
                        <div className="activityDetailsModal__categ">{categorie}</div>
                    ) : null}
                    <h2 id={titleId} className="activityDetailsModal__title">
                        {mainTitle}
                    </h2>
                    {modalSubtitleLine ? (
                        <p className="activityDetailsModal__subtitle">{modalSubtitleLine}</p>
                    ) : null}

                    {timeLine || showPlaceBlock ? (
                        <div className="activityDetailsModal__metaStack">
                            {timeLine ? (
                                <div className="activityDetailsModal__meta activityDetailsModal__meta--time">
                                    <span className="activityDetailsModal__metaIcon" aria-hidden>
                                        <Clock />
                                    </span>
                                    <span className="activityDetailsModal__metaText activityDetailsModal__metaText--muted">
                                        {timeLine}
                                    </span>
                                </div>
                            ) : null}
                            {showPlaceBlock ? (
                                <div className="activityDetailsModal__meta activityDetailsModal__meta--place activityDetailsModal__meta--placeBlock">
                                    <span className="activityDetailsModal__metaIcon" aria-hidden>
                                        <MapLinkIcon />
                                    </span>
                                    <div className="activityDetailsModal__placeLines">
                                        {mapsSearch ? (
                                            <a
                                                className="activityDetailsModal__metaText activityDetailsModal__metaText--link activityDetailsModal__placePrimary"
                                                href={
                                                    'https://www.google.com/maps/search/' +
                                                    encodeURIComponent(mapsSearch)
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {primaryPlaceLabel}
                                            </a>
                                        ) : (
                                            <span className="activityDetailsModal__metaText activityDetailsModal__metaText--muted activityDetailsModal__placePrimary">
                                                {primaryPlaceLabel}
                                            </span>
                                        )}
                                        {addressUnderName ? (
                                            <span className="activityDetailsModal__metaText activityDetailsModal__metaText--muted activityDetailsModal__placeAddressDetail">
                                                {addressUnderName}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {rainLine ? (
                        <p className="activityDetailsModal__rain">{rainLine}</p>
                    ) : null}

                    {animTrim ? (
                        <section
                            className="activityDetailsModal__section"
                            aria-label="Animateur"
                        >
                            <h3 className="activityDetailsModal__sectionTitle">Animateur</h3>
                            <p className="activityDetailsModal__sectionLead">{animTrim}</p>
                        </section>
                    ) : null}

                    {hasArtistStrip ? (
                        <section
                            className="activityDetailsModal__section activityDetailsModal__section--artistsHead"
                            aria-label="Artistes"
                        >
                            <div className="activityDetailsModal__artistsHeadRow">
                                <h3 className="activityDetailsModal__sectionTitle">Artistes</h3>
                                {overflows ? (
                                    <div className="activityDetailsModal__carouselNav">
                                        <button
                                            type="button"
                                            className="activityDetailsModal__carouselBtn"
                                            onClick={() => scrollArtists(-1)}
                                            aria-label="Artistes précédents"
                                            aria-controls={artistStripId}
                                        >
                                            <ActivityChevronRight
                                                className="activityDetailsModal__carouselIcon activityDetailsModal__carouselIcon--prev"
                                                aria-hidden
                                            />
                                        </button>
                                        <button
                                            type="button"
                                            className="activityDetailsModal__carouselBtn"
                                            onClick={() => scrollArtists(1)}
                                            aria-label="Artistes suivants"
                                            aria-controls={artistStripId}
                                        >
                                            <ActivityChevronRight
                                                className="activityDetailsModal__carouselIcon"
                                                aria-hidden
                                            />
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </section>
                    ) : null}
                </div>
                {hasArtistStrip ? (
                    <div
                        className="activityDetailsModal__artistBleed"
                        onKeyDown={onStripKeyDown}
                    >
                        {artistSlides.length > 1 ? (
                            <p
                                id={artistLiveId}
                                className="sr-only"
                                aria-live="polite"
                                aria-atomic="true"
                            >
                                {srCarouselArtist}
                            </p>
                        ) : null}
                        <div className="activityDetailsModal__stripWrap">
                            <div
                                id={artistStripId}
                                ref={stripRef}
                                className={
                                    'activityDetailsModal__strip' +
                                    (artistSlides.length === 1
                                        ? ' activityDetailsModal__strip--singleArtist'
                                        : '')
                                }
                                tabIndex={0}
                                role="region"
                                aria-label="Liste des artistes — utilisez les flèches gauche et droite pour faire défiler"
                            >
                                {artistSlides.map((slide) => (
                                    <article
                                        key={slide.name}
                                        className="activityDetailsModal__artistCard"
                                    >
                                        <figure className="activityDetailsModal__thumb">
                                            <div className="activityDetailsModal__artistImageWrap">
                                                {slide.src ? (
                                                    <LazyLoadImage
                                                        src={slide.src}
                                                        alt={slide.name}
                                                        className="activityDetailsModal__artistImage"
                                                    />
                                                ) : (
                                                    <div
                                                        className="activityDetailsModal__artistPlaceholder"
                                                        aria-hidden
                                                    />
                                                )}
                                            </div>
                                            {slide.credit ? (
                                                <figcaption className="credit">
                                                    {slide.credit}
                                                </figcaption>
                                            ) : null}
                                        </figure>
                                        <div className="activityDetailsModal__artistCaption">
                                            <p className="activityDetailsModal__artistName">
                                                {slide.name}
                                            </p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : null}
                {showEventActivityImageOnly ? (
                    <div className="activityDetailsModal__artistBleed">
                        <div className="activityDetailsModal__stripWrap">
                            <div className="activityDetailsModal__strip activityDetailsModal__strip--singleArtist">
                                <article className="activityDetailsModal__artistCard">
                                    <figure className="activityDetailsModal__thumb">
                                        <div className="activityDetailsModal__artistImageWrap">
                                            <LazyLoadImage
                                                src={eventActivityImage.src}
                                                alt={
                                                    mainTitle
                                                        ? `Illustration : ${mainTitle}`
                                                        : "Illustration de l'activité"
                                                }
                                                className="activityDetailsModal__artistImage"
                                            />
                                        </div>
                                        {eventActivityImage.credit ? (
                                            <figcaption className="credit">
                                                {eventActivityImage.credit}
                                            </figcaption>
                                        ) : null}
                                    </figure>
                                </article>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};

export default ActivityDetailsModal;
